import React, { useState, useRef, useEffect } from "react";
import { dropDown } from "../asset";
import { FaChevronLeft } from "react-icons/fa";
import { FaChevronRight } from "react-icons/fa";

interface PaginationBarProps {
  currentPage: number;
  pageSize: number;
  totalItems?: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange?: (newSize: number) => void;
}

const PaginationBar: React.FC<PaginationBarProps> = ({
  currentPage,
  pageSize,
  totalItems = 0,
  onPageChange,
  onPageSizeChange,
}) => {
  const totalPages = Math.ceil(totalItems / pageSize);
  const startItem = totalItems === 0 ? 0 : currentPage * pageSize + 1;
  const endItem = Math.min((currentPage + 1) * pageSize, totalItems);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const pageSizes = [25, 50, 100, 150, 200];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="flex gap-10 font-medium items-center justify-end relative">
      <div className="flex items-center gap-4 relative">
        {/* Pagination Info */}
        <span
          onClick={() => setShowDropdown(!showDropdown)}
          className="text-sm font-medium text-neutral-400 cursor-pointer flex items-center gap-1 relative"
        >
          <span className="font-medium mr-2 hidden md:block">Showing : </span>
          <span className="text-primary-600">
            {startItem}-{endItem}
          </span>
          <img
            src={dropDown}
            alt="play-icon"
            className="pr-1 shrink-0 cursor-pointer"
          />
          <span className="text-neutral-400 font-normal text-sm mx-1">
            of {totalItems}
          </span>
          {/* Dropdown */}
          {showDropdown && (
            <div
              ref={dropdownRef}
              className="absolute top-[120%] left-0 bg-white border rounded shadow-md z-50 text-sm w-20"
            >
              {pageSizes.map((size) => (
                <button
                  key={size}
                  onClick={() => {
                    onPageSizeChange?.(size);
                    setShowDropdown(false);
                  }}
                  className={`block w-full text-black px-3 py-2 text-left hover:bg-gray-100 ${
                    size === pageSize ? "bg-gray-100 font-bold" : ""
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          )}
        </span>

        {/* Prev & Next buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 0}
            className={`p-3 rounded-lg cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
              currentPage === 0 ? "bg-white" : "bg-primary-500 text-white"
            }`}
          >
            <FaChevronLeft />
          </button>

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages - 1}
            className={`p-3 rounded-lg cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
              currentPage >= totalPages - 1
                ? "bg-white"
                : "bg-primary-500 text-white"
            }`}
          >
            <FaChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaginationBar;
